<template>
  <BaseModal :modalTitle="$t('W: Tilføj bilag')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <UiInputSelectVee
        name="locale"
        :label="$t('W: Sprog')"
        :unselectedText="$t('W: Vælg sprog...')"
        :options="languageOptions"
        :rules="['required']"
        isRequired
        v-model="form.locale"
        @update:model-value="resetValidationMessages" />

      <div class="mb-2 flex flex-col gap-1.5">
        <span class="text-sm font-medium text-text-primary">
          {{ $t('W: PDF-fil') }}
        </span>
        <UiFileUpload
          ref="fileUpload"
          :modelValue="files"
          :allowedFileTypes="['pdf']"
          :maxFiles="1"
          @update:model-value="onFilesUpdate"
          @on-file-selected="onFileSelected" />
      </div>

      <UiInputTextFieldVee
        name="internalTitle"
        :label="$t('W: Internt navn')"
        :placeholder="$t('W: Til internt brug')"
        :rules="['required']"
        isRequired
        v-model="form.internalTitle"
        @update:model-value="resetValidationMessages" />

      <UiInputTextFieldVee
        name="displayTitle"
        :label="$t('W: Vist navn')"
        :placeholder="$t('W: Navn brugt i filnavnet')"
        :rules="['required']"
        :hint="displayTitleHint"
        isRequired
        v-model="form.displayTitle"
        @update:model-value="resetValidationMessages" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useAccountStore } from '@/stores/accountStore'
import { useBilagLibraryItemStore } from '@/stores/bilagLibraryItemStore'
import { useUserStore } from '@/stores/userStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { generateFirestoreId, stripFileExtension } from '@/globals/typescript/utils/utils'
import { LAB_CUSTOMER_LANGUAGES } from '@team-milva/lab-models/language'
import type { IFileBase } from '@team-milva/lab-models/files'
import type FileUpload from '@/components/ui/FileUpload.vue'
import { sanitizeFilenameSegment } from '@team-milva/lab-utils/filename'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

type TCreateBilagLibraryItemForm = {
  internalTitle: string
  displayTitle: string
  locale: string
}

function createForm(): TCreateBilagLibraryItemForm {
  return { internalTitle: '', displayTitle: '', locale: '' }
}

export default {
  name: 'CreateBilagLibraryItemModal',

  emits: ['close'],

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      files: [] as IFileBase[],
      isLoading: false,
      errorText: '',
      bilagLibraryItemID: generateFirestoreId(),
    }
  },

  computed: {
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useUserStore, ['currentUser']),

    languageOptions(): { value: string; text: string }[] {
      return Object.values(LAB_CUSTOMER_LANGUAGES)
        .map((lang) => ({ value: lang.locale, text: lang.name }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    exactUploadPath(): string {
      return `labAccounts/${this.labAccount?.id ?? ''}/bilagLibrary/${this.bilagLibraryItemID}.pdf`
    },

    displayTitleHint(): string {
      if (!this.form.displayTitle) return ''
      return this.$t('W: Vises som: {name}.pdf', {
        name: sanitizeFilenameSegment(this.form.displayTitle),
      })
    },
  },

  methods: {
    ...mapActions(useBilagLibraryItemStore, ['createBilagLibraryItem']),

    onFilesUpdate(files: IFileBase[]): void {
      this.files = files
      this.resetValidationMessages()
    },

    onFileSelected(file: File): void {
      const name = stripFileExtension(file.name)
      this.form.displayTitle = name
      this.form.internalTitle = name
    },

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.resetValidationMessages()

      try {
        if (!this.currentUser?.id || !this.labAccount?.id) {
          this.errorText = this.$t('error.common.standard')
          return
        }

        const uploaded = await (
          this.$refs.fileUpload as InstanceType<typeof FileUpload>
        ).uploadPending(this.exactUploadPath)
        const storagePath = uploaded[0]?.path

        if (!storagePath) {
          this.errorText = this.$t('W: Vælg en PDF-fil')
          return
        }

        const result = await this.createBilagLibraryItem({
          bilagLibraryItemID: this.bilagLibraryItemID,
          internalTitle: this.form.internalTitle,
          displayTitle: this.form.displayTitle,
          locale: this.form.locale,
          storagePath,
          createdByUserID: this.currentUser.id,
        })

        if (result.success) {
          this.unsavedGuard.takeSnapshot()
          useToast().success(this.$t('W: Bilag oprettet'))
          this.$emit('close')
          return
        }

        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette bilag'))
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette bilag'))
      } finally {
        this.isLoading = false
      }
    },

    formResetCallback(): boolean {
      return !this.errorText
    },

    resetValidationMessages(): void {
      this.errorText = ''
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
